import { Link } from 'react-router-dom';
import { 
  Leaf, Phone, MessageCircle, CheckCircle, ArrowRight, 
  Scissors, TreeDeciduous, Sprout, Package, Truck, Calendar,
  Star, Clock, MapPin, Shield, Flame
} from 'lucide-react';
import { WHATSAPP_HREF, PHONE_DISPLAY, REVIEWS } from '../config/contact';
import { STATS } from '../config/business';
import RealizaceShowcase from '../components/RealizaceShowcase';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEOHead, { SCHEMAS } from '../components/SEOHead';
import Reveal from '../components/Reveal';
import CountUp from '../components/CountUp';
import PartnersSection from '../components/PartnersSection';

const MORE_SERVICES = [
  { to: '/sekani-prerostle-travy', label: 'Sekání přerostlé trávy' },
  { to: '/kaceni-stromu', label: 'Kácení stromů' },
  { to: '/strihani-tuji-cena', label: 'Stříhání tújí' },
  { to: '/cisteni-zahrady', label: 'Čištění a úklid zahrady' },
  { to: '/vertikutace-travniku', label: 'Vertikutace' },
  { to: '/pokladani-travniku', label: 'Travní koberec' },
  { to: '/realizace-zahrad', label: 'Realizace zahrad' },
  { to: '/udrzba-zahrady', label: 'Údržba zahrady' },
  { to: '/odvoz-bioodpadu', label: 'Odvoz bioodpadu' },
  { to: '/hrabani-listi', label: 'Hrabání listí' },
  { to: '/uklid-snehu', label: 'Úklid sněhu' },
  { to: '/zahradnik-dvur-kralove', label: 'Zahradník Dvůr Králové' },
];

const HomePage = () => {
  const services = [
    {
      icon: Scissors,
      title: 'Sekání trávy',
      page: '/pravidelne-sekani-travy',
      description: 'Profesionální sekání trávníků všech velikostí. Pravidelná údržba nebo jednorázová služba.',
      price: 'Cena po obhlídce',
      features: ['Sekání bez hnojení', 'Sekání s hnojením NPK', 'Hrubé sekání přerostlé trávy'],
    },
    {
      icon: TreeDeciduous,
      title: 'Stříhání keřů a kácení stromů',
      page: '/strihani-keru-kaceni-stromu',
      description: 'Stříhání a tvarování keřů, tújí a živých plotů, ořez a kácení stromů.',
      price: 'Cena po obhlídce',
      features: ['Stříhání keřů, tújí a živých plotů', 'Ořez a tvarování stromů', 'Kácení stromů a odvoz větví'],
    },
    {
      icon: Flame,
      title: 'Likvidace a čištění pozemků',
      page: '/likvidace-pozemku',
      description: 'Kompletní vyčištění zarostlých pozemků, parcel a zahrad – křoviny, nálety, přerostlá vegetace.',
      price: 'Cena po obhlídce',
      features: ['Kácení křovin a náletů', 'Sekání a mulčování vysoké trávy', 'Odvoz a ekologická likvidace'],
    },
  ];

  const steps = [
    { icon: MessageCircle, title: 'Pošlete poptávku', description: 'Formulář, WhatsApp nebo telefon' },
    { icon: MapPin, title: 'Bezplatná obhlídka', description: 'Přijedeme a připravíme kalkulaci' },
    { icon: CheckCircle, title: 'Cena předem', description: 'Vždy znáte cenu před zahájením' },
    { icon: Truck, title: 'Práce hotova', description: 'Rychle, spolehlivě a kvalitně' },
  ];

  const stats = STATS;

  const faqItems = [
    {
      question: 'Jak zjistím cenu za sekání zahrady ve Dvoře Králové?',
      answer: 'Stačí nás kontaktovat – přijedeme na bezplatnou obhlídku vaší zahrady nebo pozemku a na místě vám sdělíme přesnou cenu. Žádné překvapení, cenu vždy znáte předem.',
    },
    {
      question: 'Co všechno děláte? Jaké služby nabízíte?',
      answer: 'Nabízíme sekání trávy, stříhání keřů, živých plotů a tújí, ořez a kácení stromů, realizace zahrad (návrh a založení trávníku), vertikutaci, hnojení, sezónní balíčky (jaro, léto, podzim, zima), likvidaci zarostlých pozemků, odvoz odpadu a celoroční VIP péči.',
    },
    {
      question: 'Stříháte keře, živé ploty a túje?',
      answer: 'Ano. Stříháme a tvarujeme okrasné keře, túje i živé ploty všech výšek – pravidelně i jednorázově. Součástí je úklid a odvoz nebo štěpkování větví.',
    },
    {
      question: 'Provádíte kácení a ořez stromů?',
      answer: 'Provádíme ořez, prořez i kompletní kácení stromů včetně rizikového kácení po částech u domů a vedení. Větve nastěpkujeme nebo odvezeme a po práci uklidíme.',
    },
    {
      question: 'Co znamená "realizace zahrad"?',
      answer: 'Realizujeme zahrady na klíč – od návrhu přes terénní úpravy a založení trávníku (ze semene i z travního koberce) až po výsadbu stromů, keřů a záhonů. Vytvoříme novou zahradu i proměníme stávající.',
    },
    {
      question: 'Pokládáte travní koberec i zakládáte trávník ze semene?',
      answer: 'Ano. Nabízíme pokládku travního koberce (rolovaného trávníku) s okamžitě zeleným výsledkem i levnější zakládání trávníku ze semene. Připravíme podloží, položíme nebo zaséjeme a poradíme s péčí a zálivkou. Travní koberec pokládáme do 24 hodin od sloupnutí.',
    },
    {
      question: 'Jaké oblasti pokrýváte vašimi službami?',
      answer: 'Rozšířili jsme dojezd na 50 km od Dvora Králové nad Labem – nově obsluhujeme i krajské město Hradec Králové. Pokrýváme Hradec Králové, Trutnov, Vrchlabí, Hostinné, Jaroměř, Náchod, Hořice, Jičín a okolní obce.',
    },
    {
      question: 'Co je "Likvidace a čištění pozemků"?',
      answer: 'Služba pro pozemky, parcely a zahrady silně zarostlé křovinami, náletovými dřevinami nebo přerostlou vegetací. Provádíme kompletní vyčištění – kácení, mulčování, odvoz odpadu a finální úklid.',
    },
  ];


  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOHead 
        title="Sekání trávy a zahradnické služby Dvůr Králové | SeknuTo.cz"
        description="Sekání trávy, údržba zahrad, stříhání keřů a likvidace zarostlých pozemků ve Dvoře Králové nad Labem a okolí do 50 km. Obhlídka a nacenění zdarma."
        keywords="sekání trávy Dvůr Králové, zahradnické služby, údržba zahrad, likvidace pozemků, čištění parcel, vertikutace trávníku, sekání trávy Trutnov, sekání trávy Vrchlabí, zahradník Dvůr Králové"
        schema={[SCHEMAS.localBusiness, SCHEMAS.faqPage(faqItems)]}
      />
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-28 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F0FDF4] via-white to-[#F8FAFC]" />
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232E8B3E' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <Reveal variant="reveal-left" className="text-left">
              <div className="inline-flex items-center gap-2 bg-white border-2 border-[#2E8B3E]/20 rounded-full px-4 py-2 mb-6 shadow-sm">
                <Star className="w-4 h-4 text-[#2E8B3E]" fill="#2E8B3E" />
                <span className="text-sm font-semibold text-[#374151]">
                  Mladý tým s chutí do práce
                </span>
              </div>
              
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#111827] leading-[1.1] mb-6"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Perfektní sekání{' '}
                <span className="text-[#2E8B3E]">vašeho trávníku</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-[#374151] mb-8 leading-relaxed max-w-xl font-medium">
                Sekání, údržba zahrad, likvidace zarostlých pozemků – rychle a za férové ceny. 
                Spoléhejte na mladý, energický tým z Dvora Králové nad Labem.
              </p>

              {/* Trust badges */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-5 mb-8">
                {[
                  { icon: CheckCircle, text: 'Rychlost a spolehlivost' },
                  { icon: Shield, text: 'Cena vždy předem' },
                  { icon: MapPin, text: 'Místní firma' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm font-semibold text-[#374151]">
                    <item.icon className="w-5 h-5 text-[#2E8B3E]" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-track-location="hero">
                <Link to="/rezervace" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    className="w-full bg-[#2E8B3E] hover:bg-[#256d31] text-white rounded-full px-8 h-14 text-base font-bold shadow-lg hover:shadow-xl transition-all"
                    data-testid="hero-cta-rezervace"
                  >
                    Nezávazná poptávka zdarma
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="w-full border-2 border-[#2E8B3E] text-[#2E8B3E] hover:bg-[#F0FDF4] rounded-full px-8 h-14 text-base font-bold"
                    data-testid="hero-cta-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp: {PHONE_DISPLAY}
                  </Button>
                </a>
              </div>
              <p className="text-sm text-gray-600 mt-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#2E8B3E] shrink-0" aria-hidden="true" />
                Obhlídka a nacenění zdarma · ozveme se do 24 hodin
              </p>
            </Reveal>

            {/* Right - Stats Card */}
            <Reveal variant="reveal-right" delay={180} className="relative mt-8 lg:mt-0">
              <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 border-2 border-gray-100 lift-hover">
                <div className="flex items-center gap-4 mb-6">
                  <div className="icon-pop w-14 h-14 sm:w-16 sm:h-16 bg-[#2E8B3E] rounded-2xl flex items-center justify-center shadow-lg">
                    <Leaf className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <div>
                    {/* <p>, ne <h3>: v hero by nadpis přeskočil úroveň H1 → H3 */}
                    <p className="font-bold text-lg sm:text-xl text-[#111827]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Pěkně udržovaný trávník
                    </p>
                    <p className="text-[#374151] font-medium">Profesionální sekání a péče</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 sm:gap-6 py-6 border-t-2 border-b-2 border-gray-100">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl md:text-3xl font-black text-[#2E8B3E]" style={{ fontFamily: 'Poppins, sans-serif' }}>Zdarma</p>
                    <p className="text-xs sm:text-sm font-semibold text-[#6B7280]">Obhlídka</p>
                  </div>
                  <div className="text-center border-x-2 border-gray-100">
                    <p className="text-xl sm:text-2xl md:text-3xl font-black text-[#2E8B3E]" style={{ fontFamily: 'Poppins, sans-serif' }}>50 km</p>
                    <p className="text-xs sm:text-sm font-semibold text-[#6B7280]">Dosah</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl md:text-3xl font-black text-[#2E8B3E]" style={{ fontFamily: 'Poppins, sans-serif' }}>Zdarma</p>
                    <p className="text-xs sm:text-sm font-semibold text-[#6B7280]">Kalkulace</p>
                  </div>
                </div>

                <div className="mt-6">
                  <Link to="/rezervace">
                    <Button 
                      className="w-full bg-[#2E8B3E] hover:bg-[#256d31] text-white rounded-xl h-12 sm:h-14 font-bold text-base"
                      data-testid="hero-card-cta"
                    >
                      Získat cenovou nabídku zdarma
                    </Button>
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Fotky před/po ze skutečných zakázek – hned pod hero */}
      <RealizaceShowcase />

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#111827] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Naše služby
            </h2>
            <p className="text-[#374151] max-w-2xl mx-auto text-base sm:text-lg font-medium">
              Kompletní zahradnické služby od sekání trávníků po likvidaci zarostlých pozemků.
              Profesionální přístup a férovost.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, idx) => (
              <Reveal key={idx} delay={idx * 110} variant="reveal-scale">
              <Card
                className="card-hover bg-white border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-[#2E8B3E]/30 h-full"
                data-testid={`service-card-${idx}`}
              >
                <CardContent className="p-5 sm:p-6 md:p-8">
                  <div className="icon-pop w-12 h-12 sm:w-14 sm:h-14 bg-[#2E8B3E] rounded-xl flex items-center justify-center mb-5 shadow-md">
                    <service.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#111827] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <Link to={service.page} className="hover:text-[#2E8B3E] hover:underline">{service.title}</Link>
                  </h3>
                  <p className="text-[#374151] mb-4 text-sm leading-relaxed font-medium">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-2 text-sm text-[#374151] font-medium">
                        <CheckCircle className="w-4 h-4 text-[#2E8B3E] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t-2 border-gray-100">
                    <span className="text-sm font-semibold text-[#6B7280]">Cena:</span>
                    <span className="text-lg font-black text-[#2E8B3E]">{service.price}</span>
                  </div>
                  <Link to="/rezervace" className="block mt-4">
                    <Button 
                      variant="outline" 
                      className="w-full border-2 border-[#2E8B3E] text-[#2E8B3E] hover:bg-[#F0FDF4] rounded-lg font-bold"
                    >
                      Nezávazná poptávka
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              </Reveal>
            ))}
          </div>

          {/* Všechny služby – přímé odkazy na jejich stránky */}
          <nav className="mt-10" aria-label="Další služby">
            <ul className="flex flex-wrap justify-center gap-2.5">
              {MORE_SERVICES.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-flex items-center px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-[#374151] hover:border-[#2E8B3E] hover:text-[#1B4332] hover:bg-[#F0FDF4] transition-all">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="text-center mt-10">
            <Link to="/sluzby">
              <Button 
                className="bg-[#2E8B3E] hover:bg-[#256d31] text-white rounded-full px-8 h-12 font-bold"
                data-testid="view-all-services-btn"
              >
                Zobrazit všechny služby
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#111827] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Proč si vybrat naše služby?
            </h2>
            <p className="text-[#374151] max-w-2xl mx-auto text-base sm:text-lg font-medium">
              Mladý tým s energií a profesionálním přístupem.
              Cenu znáte vždy předem, bez skrytých poplatků.
            </p>
          </Reveal>

          {/* How it works */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-16">
            {steps.map((step, idx) => (
              <Reveal key={idx} delay={idx * 100} className="text-center group" data-testid={`step-${idx}`}>
                <div className="icon-pop w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md border border-gray-100">
                  <step.icon className="w-7 h-7 text-[#3FA34D]" />
                </div>
                <p className="text-sm text-[#9CA3AF] mb-1">{idx + 1}.</p>
                <h3 className="font-semibold text-[#222222] mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-sm text-[#4B5563]">{step.description}</p>
              </Reveal>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <Reveal
                key={idx}
                delay={idx * 90}
                variant="reveal-scale"
                className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100 lift-hover"
                data-testid={`stat-${idx}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-[#3FA34D] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <CountUp value={stat.value} />
                </p>
                <p className="text-sm text-[#4B5563]">{stat.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Hodnocení – jen ze skutečného, ověřitelného zdroje.
          Dřív tu bylo 6 recenzí datovaných 2024, tedy před vznikem webu, a text
          „5.0 ze 5 – přes 20 recenzí". Skutečnost na Firmy.cz je jiná a zákon
          o ochraně spotřebitele vymyšlené recenze zakazuje. */}
      <section className="py-16 md:py-24 bg-[#F0FDF4]" data-testid="reviews-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest text-[#3FA34D] uppercase mb-3 bg-[#3FA34D]/10 px-4 py-1.5 rounded-full">
              Hodnocení zákazníků
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#222222] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Co říkají naši zákazníci
            </h2>
            <div className="inline-flex flex-col items-center bg-white rounded-2xl px-8 py-6 shadow-sm border border-gray-100">
              <p className="text-5xl font-bold text-[#1B4332]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {String(REVIEWS.rating).replace('.', ',')}
                <span className="text-2xl text-gray-400 font-semibold"> / 5</span>
              </p>
              <div className="flex items-center gap-1 mt-2" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < Math.floor(REVIEWS.rating) ? 'fill-amber-400 text-amber-400' : 'text-amber-300'}`} />
                ))}
              </div>
              <p className="text-[#4B5563] text-sm mt-2">{REVIEWS.count} hodnocení na {REVIEWS.label}</p>
              <a
                href={REVIEWS.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#2E8B3E] hover:bg-[#256d31] text-white font-semibold px-6 h-11 transition-colors"
                data-testid="reviews-source-link"
              >
                Přečíst recenze na {REVIEWS.label}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
            <p className="text-xs text-gray-500 mt-5 max-w-xl mx-auto">
              Hodnocení pochází z nezávislého katalogu {REVIEWS.label}, kde ho mohou
              zanechat jen jeho přihlášení uživatelé. Na tomto webu recenze nevybíráme ani
              neupravujeme – všechny najdete přímo u zdroje.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Spolupráce s firmami a živnostníky (data v config/partners.js) */}
      <PartnersSection />

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#222222] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Často kladené otázky
            </h2>
            <p className="text-[#4B5563]">
              Odpovědi na nejčastější otázky o našich službách a praktické rady pro vaši zahradu.
            </p>
          </Reveal>

          <Reveal>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`}
                className="bg-[#F9FAFB] rounded-xl border-none px-6"
                data-testid={`faq-item-${idx}`}
              >
                <AccordionTrigger className="text-left font-medium text-[#222222] hover:no-underline py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#4B5563] pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Firmy.cz Banner */}
      <section className="py-8 bg-white" data-testid="firmy-cz-banner">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <a 
            href="https://www.firmy.cz/detail/13988364-seknuto-cz-dvur-kralove-nad-labem.html#pridat-hodnoceni" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="firmy-cz-link"
          >
            <img
              width="249"
              height="68"
              loading="lazy"
              src="https://www.firmy.cz/img/widgets/firmy-ohodnotte-nas-tmave.svg"
              alt="SeknuTo.cz na Firmy.cz"
              className="inline-block hover:opacity-80 transition-opacity"
            />
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#222222]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Chcete zahradu jako z katalogu?
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            Pošlete nezávaznou poptávku a my vám připravíme kalkulaci na míru. 
            Rychle, spolehlivě a s cenou předem.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/rezervace">
              <Button 
                size="lg"
                className="bg-[#3FA34D] hover:bg-[#2d7a38] text-white rounded-full px-10 h-14 text-base font-semibold"
                data-testid="cta-rezervace"
              >
                Získat nezávaznou kalkulaci
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:+420730588372">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-10 h-14 text-base font-semibold"
                data-testid="cta-phone"
              >
                <Phone className="w-5 h-5 mr-2" />
                730 588 372
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
